/// AR Engine Agnostic - Modular Scene Architecture
/// Supports Zappar and MindAR for image tracking with multiple reusable scenes

import * as THREE from 'three';
import './index.css';
import packageJson from '../package.json';

// Import AR engine system
import { IAREngine } from './ar-engine/ar-engine.interface';
import { AREngineFactory } from './ar-engine/ar-engine-factory';
import { AREngineType, TrackerState, LoadingProgressCallback } from './ar-engine/ar-types';
import { createARConfig, logARConfig, getTargetFilePath } from './ar-config';

// Import card orientation standards
import {
  CardOrientation,
  getCardDimensions,
  getPortalRotation,
  getCardScale,
  CardNaming,
  createCardTriggerConfig
} from './card-orientation';

// Import scene system
import { SceneManager } from './scene-manager';
import { TriggerManager } from './trigger-manager';
import { ClippingScene } from './scenes/clipping-scene';
import { GLBAnimationScene } from './scenes/glb-animation-scene';
import { PortalScene } from './scenes/portal-scene';
import { VoodooShowcaseScene } from './scenes/voodoo-showcase-scene';
import { VideoCardScene } from './scenes/video-card-scene';
import { CardOutlineScene } from './scenes/card-outline-scene';
import { StandaloneController } from './standalone-controller';
import { createCyberHud } from './hud/cyber-hud';

// Asset URLs
const modelUrl = new URL('./assets/waving.glb', import.meta.url).href;
const videoOverlayUrl = new URL('./assets/video/Card-Ar-Promo.mp4', import.meta.url).href;
const voodooLogoUrl = new URL('./assets/voodoo-logo.glb', import.meta.url).href;

// Parse URL parameters
const urlParams = new URLSearchParams(window.location.search);
const standaloneScene = urlParams.get('scene');
const isStandaloneMode = standaloneScene !== null;

// Portrait scale configuration
// Default: 1.5x (50% larger) to compensate for portrait card's smaller width (0.65 vs 1.0)
// Can be overridden via URL parameter for testing: ?portraitScale=1.6
const portraitScaleParam = urlParams.get('portraitScale');
const portraitScaleMultiplier = portraitScaleParam ? parseFloat(portraitScaleParam) : 1.5;
// Note: Standard value is also defined in card-orientation.ts as CARD_SCALE_MULTIPLIERS

// Get AR configuration from URL parameters
const arConfig = createARConfig(urlParams);

// Get the appropriate target file based on AR engine
const targetImageUrl = getTargetFilePath('voodoo-card-1-front', arConfig.engineType);

// Log AR configuration
if (!isStandaloneMode) {
  logARConfig(arConfig, targetImageUrl);

  // Log portrait scale debug parameter if overridden
  if (portraitScaleParam) {
    console.log(`🔍 DEBUG: Portrait scale override = ${portraitScaleMultiplier}x`);
  }
}

// DRY helper to create overlay text
const instructionBanner = document.createElement('div');
instructionBanner.style.position = 'absolute';
instructionBanner.style.bottom = '20px';
instructionBanner.style.left = '50%';
instructionBanner.style.transform = 'translateX(-50%)';
instructionBanner.style.padding = '0.75em 1.25em';
instructionBanner.style.background = 'rgba(0, 0, 0, 0.7)';
instructionBanner.style.color = '#fff';
instructionBanner.style.fontFamily = `'Amatic SC', sans-serif`;
instructionBanner.style.fontSize = '2rem';
instructionBanner.style.letterSpacing = '0.08em';
instructionBanner.style.border = '2px solid #fff';
instructionBanner.style.borderRadius = '12px';
instructionBanner.style.zIndex = '999';
instructionBanner.textContent = 'Papa Legba says find your Voodoo card.';

// Version number display (bottom left)
const versionDisplay = document.createElement('div');
versionDisplay.style.position = 'fixed';
versionDisplay.style.bottom = '10px';
versionDisplay.style.left = '10px';
versionDisplay.style.padding = '4px 8px';
versionDisplay.style.background = 'rgba(0, 0, 0, 0.5)';
versionDisplay.style.color = '#888';
versionDisplay.style.fontFamily = 'monospace';
versionDisplay.style.fontSize = '10px';
versionDisplay.style.borderRadius = '4px';
versionDisplay.style.zIndex = '1000';
versionDisplay.textContent = `v${packageJson.version}`;

// Setup renderer and scene
// Note: For MindAR, these will be replaced with MindAR's own instances after tracker creation
let renderer = new THREE.WebGLRenderer({ antialias: true, stencil: true });
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.shadowMap.enabled = true;
renderer.localClippingEnabled = true;  // Enable clipping planes for portal scene
document.body.appendChild(renderer.domElement);
if (!isStandaloneMode) {
  document.body.appendChild(instructionBanner);
}
document.body.appendChild(versionDisplay);

let hud: ReturnType<typeof createCyberHud> | null = null;

let scene = new THREE.Scene();

// Handle window resize
window.addEventListener('resize', () => {
  renderer.setSize(window.innerWidth, window.innerHeight);
});

// ============================================================================
// MAIN INITIALIZATION - Async setup for AR engine
// ============================================================================

// ============================================================================
// SCENE SETUP - Register all available scenes
// ============================================================================

const sceneManager = new SceneManager();

// Register Video Card Scene (landscape card with portrait video - rotate 90° clockwise)
const landscapeDims = getCardDimensions(CardOrientation.LANDSCAPE);
const videoCardScene = new VideoCardScene({
  videoUrl: videoOverlayUrl,
  cardWidth: landscapeDims.width,
  cardHeight: landscapeDims.height,
  textureRotationRadians: -Math.PI / 2  // Rotate clockwise to fit portrait video on landscape card
});
sceneManager.registerScene(videoCardScene);

// Register Portrait Video Card Scene (portrait video on portrait card - no rotation)
const portraitDims = getCardDimensions(CardOrientation.PORTRAIT);
const portraitVideoCardScene = new VideoCardScene({
  videoUrl: videoOverlayUrl,
  cardWidth: portraitDims.width,
  cardHeight: portraitDims.height,
  textureRotationRadians: 0, // No rotation - portrait video on portrait card
  sceneId: CardNaming.sceneId('video-card', CardOrientation.PORTRAIT),
  name: CardNaming.sceneName('Video Card Overlay', CardOrientation.PORTRAIT),
  description: 'Portrait-aligned promo video directly on the tracked card surface',
  defaultScale: new THREE.Vector3(portraitScaleMultiplier, portraitScaleMultiplier, portraitScaleMultiplier)
});
sceneManager.registerScene(portraitVideoCardScene);

// Register Card Outline Test Scenes (for debugging/verification)
const cardOutlineLandscape = new CardOutlineScene({
  cardWidth: landscapeDims.width,
  cardHeight: landscapeDims.height,
  sceneId: CardNaming.sceneId('card-outline', CardOrientation.LANDSCAPE),
  name: CardNaming.sceneName('Card Outline Test', CardOrientation.LANDSCAPE),
  description: 'Visual outline for landscape card boundary verification',
  borderColor: 0x00ff00,
  showCornerMarkers: true,
  showAxes: true,
  showDimensions: true,
  showOrientationLabel: true,
  animate: true
});
sceneManager.registerScene(cardOutlineLandscape);

const cardOutlinePortrait = new CardOutlineScene({
  cardWidth: portraitDims.width,
  cardHeight: portraitDims.height,
  // No rotation needed - portrait is just different dimensions in same X-Z plane
  sceneId: CardNaming.sceneId('card-outline', CardOrientation.PORTRAIT),
  name: CardNaming.sceneName('Card Outline Test', CardOrientation.PORTRAIT),
  description: 'Visual outline for portrait card boundary verification',
  borderColor: 0xff00ff, // Magenta for portrait to differentiate
  showCornerMarkers: true,
  showAxes: true,
  showDimensions: true,
  showOrientationLabel: true,
  animate: true
});
sceneManager.registerScene(cardOutlinePortrait);

// Register Clipping Scene
const clippingScene = new ClippingScene({
  enabled: false,
  shadows: true,  // Enable shadows
  visualize: false,
  color: 0x00ff00,
  shininess: 100,
  position: new THREE.Vector3(0, 2, -2),  // Position relative to tracking image
  scale: 1.5,
  // Text label options
  showLabel: true,
  labelText: 'Voodoo 2',
  labelPosition: new THREE.Vector3(0, 1, 0),  // Above the cubes
  labelColor: '#00ff00'
});
sceneManager.registerScene(clippingScene);

// Register GLB Animation Scene
const glbScene = new GLBAnimationScene({
  modelUrl: modelUrl,
  autoPlay: false,
  buttonEnabled: false  // Disabled - not needed for AR
});
sceneManager.registerScene(glbScene);

// Register Portal/Tunnel Scene
// Note: MindAR anchors are 1 unit wide by default
// Zappar uses different scaling, so we need AR-engine-specific configuration
const portalScene = new PortalScene({
  depth: 0.2,                    // How deep the tunnel goes
  cardWidth: landscapeDims.width,
  cardHeight: landscapeDims.height,
  tunnelColor: 0x1a1a2e,       // Dark blue-purple walls
  backgroundColor: 0x0f0f1e,   // Even darker at the bottom
  showGrid: true,              // Show grid at bottom
  animate: true,               // Enable pulsing/animation
  glowIntensity: 0.7,          // Cyan glow around edges
  defaultRotation: getPortalRotation(CardOrientation.LANDSCAPE),
  defaultScale: new THREE.Vector3(1, 1, 1),  // MindAR: no additional scaling needed
  debugHelpers: false          // Disable debug helpers for production
});
sceneManager.registerScene(portalScene);

// Detached voodoo showcase built on top of the tunnel effect (LANDSCAPE)
const voodooShowcaseScene = new VoodooShowcaseScene({
  portal: {
    depth: 0.25,
    cardWidth: landscapeDims.width,
    cardHeight: landscapeDims.height,
    tunnelColor: 0x1a1a2e,
    backgroundColor: 0x05030f,
    showGrid: true,          // Enable grid at bottom of portal
    animate: true,
    glowIntensity: 0.8,
    defaultRotation: getPortalRotation(CardOrientation.LANDSCAPE),
    defaultScale: new THREE.Vector3(1, 1, 1)
  },
  layout: {
    titlePosition: new THREE.Vector3(0, 0.22, -0.03),
    titleScale: 0.5,
    logoAnchor: new THREE.Vector3(0, 1, -0.02),
    logoRotation: new THREE.Euler(-Math.PI / 3, 0, 0),
    logoScale: 0.25,
    textPanelPosition: new THREE.Vector3(0, 0, 0.04),  // Moved down from 0.02 to -0.05
    textPanelScale: new THREE.Vector3(0.8, 0.6, 0.8)
  },
  animation: {
    // Portal opening animation (happens first)
    portalStartDelay: 0.2,     // Small delay before portal starts opening
    portalDotDuration: 0.3,    // Dot phase: stays as a point
    portalLineDuration: 0.6,   // Line phase: expands width
    portalOpenDuration: 0.8,   // Open phase: expands height

    // Total portal opening time: 0.2 + 0.3 + 0.6 + 0.8 = 1.9s
    // Element animations start after portal is open

    // Sequence: Portal (1.9s) → Logo (2.4s) → Title (3.9s) → Typewriter (5.4s)
    logoStartDelay: 2.4,       // Logo fades in after portal opens
    logoFadeDuration: 1.0,     // Takes 1s to fade in
    titleStartDelay: 3.9,      // Title fades in second
    titleFadeDuration: 1.5,    // Takes 1.5s to fade in
    typewriterStartDelay: 5.4, // Typewriter starts third
    typewriterCharDelay: 0.05, // Each character types quickly
    logoFloatAmplitude: 0.04,
    logoFloatSpeed: 1.4,
    showTitle: true,           // Enabled with delays
    showTypewriter: true,      // Enabled with delays
    showLogo: true             // Enabled with delays
  },
  titleText: 'Voodoo',
  titleFont: '"Amatic SC", sans-serif',        // Match branding font
  typewriterFont: '"Amatic SC", sans-serif',   // Match branding font
  floatingLogoUrl: voodooLogoUrl,
  typewriterCopy: "The spirits live at the edges, between the worlds - at the interface. Can you cross? Papa Legba rides the signal. Keep this card. "
});
sceneManager.registerScene(voodooShowcaseScene);

// Portrait version of voodoo showcase (PORTRAIT orientation)
const voodooShowcasePortraitScene = new VoodooShowcaseScene({
  sceneId: CardNaming.sceneId('voodoo-showcase', CardOrientation.PORTRAIT),
  name: CardNaming.sceneName('Voodoo Showcase', CardOrientation.PORTRAIT),
  description: 'Detached hero view with portal effect - Portrait orientation',
  portal: {
    depth: 0.25,
    cardWidth: portraitDims.width,
    cardHeight: portraitDims.height,
    tunnelColor: 0x1a1a2e,
    backgroundColor: 0x05030f,
    showGrid: true,
    animate: true,
    glowIntensity: 0.8,
    defaultRotation: getPortalRotation(CardOrientation.PORTRAIT),
    defaultScale: new THREE.Vector3(portraitScaleMultiplier, portraitScaleMultiplier, portraitScaleMultiplier)
  },
  layout: {
    // Portrait orientation (narrower and taller: 0.65w x 1.0h)
    // Portal uses same rotation as landscape - only dimensions differ
    titlePosition: new THREE.Vector3(0, 0.22, -0.05),  // Slightly lower on taller card
    titleScale: 0.4,                                    // Smaller for narrower card
    logoAnchor: new THREE.Vector3(0, 1, 0.35),         // Higher up on taller card
    logoRotation: new THREE.Euler(-Math.PI / 3, 0, 0),
    logoScale: 0.2,                                     // Smaller for narrower card
    textPanelPosition: new THREE.Vector3(0, 0, 0.04),
    textPanelScale: new THREE.Vector3(0.55, 0.7, 0.7)  // Narrower width for portrait
  },
  animation: {
    portalStartDelay: 0.2,
    portalDotDuration: 0.3,
    portalLineDuration: 0.6,
    portalOpenDuration: 0.8,
    logoStartDelay: 2.4,
    logoFadeDuration: 1.0,
    titleStartDelay: 3.9,
    titleFadeDuration: 1.5,
    typewriterStartDelay: 5.4,
    typewriterCharDelay: 0.05,
    logoFloatAmplitude: 0.04,
    logoFloatSpeed: 1.4,
    showTitle: true,
    showTypewriter: true,
    showLogo: true
  },
  titleText: 'Voodoo',
  titleFont: '"Amatic SC", sans-serif',
  typewriterFont: '"Amatic SC", sans-serif',
  floatingLogoUrl: voodooLogoUrl,
  typewriterCopy: "The spirits live at the edges, between the worlds - at the interface. Can you cross? Papa Legba rides the signal. Keep this card. "
});
sceneManager.registerScene(voodooShowcasePortraitScene);

// Global variables for access by developer tools
let camera: THREE.Camera;
let standaloneController: StandaloneController | null = null;
let arEngine: IAREngine | null = null;
let triggerManager: TriggerManager | null = null;

async function initialize() {

// ============================================================================
// CAMERA SETUP - Different for AR vs Standalone mode
// ============================================================================

if (isStandaloneMode) {
  // STANDALONE MODE - Use standard camera with orbit controls
  console.log('🎭 Standalone mode detected');

  // Parse camera parameters from URL
  const camX = parseFloat(urlParams.get('camX') || '0');
  const camY = parseFloat(urlParams.get('camY') || '2');
  const camZ = parseFloat(urlParams.get('camZ') || '5');
  const targetX = parseFloat(urlParams.get('targetX') || '0');
  const targetY = parseFloat(urlParams.get('targetY') || '0');
  const targetZ = parseFloat(urlParams.get('targetZ') || '0');
  const showGrid = urlParams.get('grid') === 'true';
  const showAxes = urlParams.get('axes') === 'true';
  const bgColor = urlParams.get('bg') ? parseInt(urlParams.get('bg')!, 16) : undefined;

  standaloneController = new StandaloneController(scene, renderer, {
    cameraPosition: new THREE.Vector3(camX, camY, camZ),
    cameraTarget: new THREE.Vector3(targetX, targetY, targetZ),
    showGrid: showGrid,
    showAxes: showAxes,
    backgroundColor: bgColor,
    enableControls: true
  });

  camera = standaloneController.getCamera();

} else {
  // AR MODE - Use pluggable AR engine
  console.log(`🎯 AR mode detected - Engine: ${arConfig.engineType.toUpperCase()}`);

  // Create AR engine using factory
  arEngine = AREngineFactory.create(arConfig, scene, renderer);

  // Check browser compatibility
  if (!arEngine.isCompatible()) {
    arEngine.showIncompatibilityUI();
    throw new Error('Unsupported browser');
  }

  // Initialize AR engine
  await arEngine.init(arConfig);

  // Request camera permissions
  const permissionResult = await arEngine.requestPermissions();
  if (!permissionResult.granted) {
    arEngine.showPermissionDeniedUI();
    throw new Error('Camera permission denied');
  }

  // For MindAR, camera is created after first tracker is added
  // For now, create a placeholder camera
  if (arConfig.engineType === AREngineType.MINDAR) {
    // MindAR will create camera when first tracker is added
    // Use a basic perspective camera as placeholder
    camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.01,
      1000
    );
    console.log('⏳ MindAR camera will be initialized when tracker is created');
  } else {
    // Other engines (like Zappar) have camera available immediately
    await arEngine.getCamera().start();
    camera = arEngine.getCamera().getThreeCamera();
    console.log('✅ Camera started');
  }
}

// ============================================================================
// TRIGGER SETUP - Link scenes to image trackers (AR mode only)
// ============================================================================

if (isStandaloneMode && standaloneScene) {
  // Standalone mode - preview a scene without AR trigger
  console.log(`🎭 Standalone mode: ${standaloneScene}`);

  if (sceneManager.hasScene(standaloneScene)) {
    sceneManager.initScene(standaloneScene, scene, renderer);
    sceneManager.setSceneVisible(standaloneScene, true);

    // Parse scene transform parameters from URL
    const sceneX = urlParams.get('sceneX') ? parseFloat(urlParams.get('sceneX')!) : undefined;
    const sceneY = urlParams.get('sceneY') ? parseFloat(urlParams.get('sceneY')!) : undefined;
    const sceneZ = urlParams.get('sceneZ') ? parseFloat(urlParams.get('sceneZ')!) : undefined;
    const sceneRotX = urlParams.get('rotX') ? parseFloat(urlParams.get('rotX')!) : undefined;
    const sceneRotY = urlParams.get('rotY') ? parseFloat(urlParams.get('rotY')!) : undefined;
    const sceneRotZ = urlParams.get('rotZ') ? parseFloat(urlParams.get('rotZ')!) : undefined;
    const sceneScale = urlParams.get('scale') ? parseFloat(urlParams.get('scale')!) : undefined;

    // Apply scene transform if specified
    if (sceneX !== undefined || sceneY !== undefined || sceneZ !== undefined ||
        sceneRotX !== undefined || sceneRotY !== undefined || sceneRotZ !== undefined ||
        sceneScale !== undefined) {
      sceneManager.setSceneTransform(
        standaloneScene,
        (sceneX !== undefined || sceneY !== undefined || sceneZ !== undefined)
          ? new THREE.Vector3(sceneX ?? 0, sceneY ?? 0, sceneZ ?? 0)
          : undefined,
        (sceneRotX !== undefined || sceneRotY !== undefined || sceneRotZ !== undefined)
          ? new THREE.Euler(sceneRotX ?? 0, sceneRotY ?? 0, sceneRotZ ?? 0)
          : undefined,
        sceneScale !== undefined
          ? new THREE.Vector3(sceneScale, sceneScale, sceneScale)
          : undefined
      );
    }
  } else {
    console.error(`Scene '${standaloneScene}' not found`);
    console.log('Available scenes:', sceneManager.listScenes());
  }
} else if (arEngine) {
  // AR mode - create image triggers
  console.log('🎯 AR mode: Creating multiple image triggers with priority system');
  console.log('   Engine type:', arConfig.engineType);
  console.log('   Target URL:', targetImageUrl);

  triggerManager = new TriggerManager(arEngine, scene, sceneManager);
  console.log('✅ TriggerManager created');

  // Define trigger configurations with priorities using standardized naming
  // NOTE: Requires multi-target .mind file with 6 images compiled together
  // Use MindAR compiler: https://hiukim.github.io/mind-ar-js-doc/tools/compile
  //
  // TESTING: To enable Card Outline Test scenes for debugging card boundaries,
  // replace the scene IDs below with 'card-outline' or 'card-outline-portrait'
  const triggerConfigs = [
   /* createCardTriggerConfig({
      baseName: 'card-vip',
      orientation: CardOrientation.LANDSCAPE,
      targetIndex: 0,
      sceneId: 'voodoo-showcase',
      priority: 60  
    }),
    createCardTriggerConfig({
      baseName: 'card-showcase',
      orientation: CardOrientation.LANDSCAPE,
      targetIndex: 1,
      sceneId: 'voodoo-showcase',
      priority: 60
    }),*/
    createCardTriggerConfig({
      baseName: 'card-showcase',
      orientation: CardOrientation.PORTRAIT,
      targetIndex: 0,
      sceneId: CardNaming.sceneId('voodoo-showcase', CardOrientation.PORTRAIT),
      priority: 70
    }),
    /*createCardTriggerConfig({
      baseName: 'card-video-promo',
      orientation: CardOrientation.LANDSCAPE,
      targetIndex: 3,
      sceneId: 'video-card',
      priority: 70
    }),
    createCardTriggerConfig({
      baseName: 'card-video-alt',
      orientation: CardOrientation.LANDSCAPE,
      targetIndex: 4,
      sceneId: 'video-card',
      priority: 70
    }), */
    createCardTriggerConfig({
      baseName: 'card-video-promo',
      orientation: CardOrientation.PORTRAIT,
      targetIndex: 1,
      sceneId: CardNaming.sceneId('video-card', CardOrientation.PORTRAIT),
      priority: 90  
    })
  ];

  if (!hud) {
    hud = createCyberHud({ totalTrackers: triggerConfigs.length, title: 'Marker Nexus' });
    hud.setStatus('Loading marker set...');
  }

  const hideBanner = () => {
    if (instructionBanner.parentElement) {
      instructionBanner.parentElement.removeChild(instructionBanner);
    }
  };

  // Create all triggers
  console.log(`📌 Creating ${triggerConfigs.length} triggers...`);
  for (const config of triggerConfigs) {
    if (hud) {
      hud.markTrackerLoading(config.id, config.name);
    }
    
    // Create progress callback for this tracker
    const onProgress: LoadingProgressCallback = (stage: string, progress: number) => {
      if (hud) {
        hud.setTrackerProgress(config.id, stage, progress);
      }
    };
    
    await triggerManager.createTrigger({
      ...config,
      targetFile: targetImageUrl,
      onFirstVisible: hideBanner,
      onProgress: onProgress
    }, renderer);
    
    if (hud) {
      hud.markTrackerLoaded(config.id);
    }
    console.log(`   ✅ Created: ${config.name}`);
  }
  console.log('✅ All triggers created');

  if (hud) {
    hud.setStatus('All markers loaded. Scanning for targets...');
    hud.startPotentialMatchScanner();
    triggerManager.onVisibilityChange(payload => {
      hud?.handleVisibility(payload);
    });
  }

  // Remove the initial popup once everything is ready
  hideBanner();

  // For MindAR, replace placeholder camera with real camera now that trackers are created
  // Also get the MindAR-owned scene and renderer
  if (arConfig.engineType === AREngineType.MINDAR) {
    console.log('🔄 Replacing scene/renderer/camera with MindAR instances...');
    camera = arEngine.getCamera().getThreeCamera();
    scene = arEngine.getScene();
    renderer = arEngine.getRenderer();
    console.log('   Camera:', !!camera);
    console.log('   Scene:', !!scene);
    console.log('   Renderer:', !!renderer);
    console.log('✅ MindAR camera, scene, and renderer initialized');
  }

  // Start AR tracking
  console.log('🎬 Starting AR tracking...');
  await arEngine.start();
  console.log('✅ AR tracking started');
  console.log('');
  console.log('🎯 Multi-Trigger Priority System Active:');
  console.log('   When multiple cards are visible, highest priority scene will display');
  console.log('   Trigger priorities (high to low):');
  triggerConfigs.forEach(config => {
    console.log(`     ${config.priority}: ${config.name} [${config.id}]`);
  });
}

// ============================================================================
// RENDER LOOP
// ============================================================================

function render(): void {
  // Update all active scenes
  sceneManager.updateActiveScenes();

  // Update camera/controls based on mode
  if (standaloneController) {
    standaloneController.update();
  } else if (arEngine) {
    arEngine.update(renderer);
  }

  // Render the scene with current renderer/scene/camera
  // (For MindAR, these are the MindAR-owned instances after initialization)
  renderer.render(scene, camera);

  // Next frame
  requestAnimationFrame(render);
}

// Start rendering
render();

} // End of initialize()

// ============================================================================
// DEVELOPER TOOLS - Exposed to window for debugging
// ============================================================================

// Call initialize function
initialize().catch(error => {
  console.error('❌ Initialization failed:', error);
});

// Expose managers for debugging in console (defined globally for console access)
(window as any).sceneManager = sceneManager;
(window as any).triggerManager = triggerManager;
(window as any).standaloneController = standaloneController;
(window as any).THREE = THREE;

// Helper functions
(window as any).setCameraPosition = (x: number, y: number, z: number) => {
  if (standaloneController) {
    standaloneController.setCameraPosition(x, y, z);
  } else {
    console.warn('Camera positioning only available in standalone mode');
  }
};

(window as any).setScenePosition = (sceneId: string, x: number, y: number, z: number) => {
  sceneManager.setSceneTransform(sceneId, new THREE.Vector3(x, y, z));
};

(window as any).focusOnScene = (sceneId: string) => {
  if (standaloneController) {
    const center = sceneManager.getSceneCenter(sceneId);
    if (center) {
      standaloneController.focusOn(center, 5);
    }
  }
};

// Log available commands
console.log(`
🛠️  Developer Tools Available:

   ${isStandaloneMode ? 'STANDALONE MODE' : 'AR MODE'}

   View available scenes:
   > sceneManager.listScenes()

   ${triggerManager ? `View active triggers:
   > triggerManager.listTriggers()

   Switch scene on trigger:
   > triggerManager.rebindScene('main-trigger', 'glb-animation', renderer)
   ` : ''}

   ${standaloneController ? `Camera controls:
   > setCameraPosition(x, y, z)
   > standaloneController.setCameraTarget(x, y, z)
   > standaloneController.resetCamera()
   > standaloneController.toggleGrid()
   > standaloneController.toggleAxes()

   Scene controls:
   > setScenePosition('clipping-demo', x, y, z)
   > focusOnScene('clipping-demo')
   ` : ''}

   Preview scene standalone:
   > ?scene=clipping-demo
   > ?scene=glb-animation
   > ?scene=card-outline           (test landscape card boundaries)
   > ?scene=card-outline-portrait  (test portrait card boundaries)

   URL Parameters (standalone mode):
   Camera: camX, camY, camZ, targetX, targetY, targetZ
   Scene: sceneX, sceneY, sceneZ, rotX, rotY, rotZ, scale
   Helpers: grid=true, axes=true, bg=333333

   URL Parameters (AR tracking):
   Debug: portraitScale=1.5 (default), portraitScale=1.3 (reduce scale), etc.
   Tracking smoothness (reduce jitter):
     - filterMinCF: 0.1 (default), higher = smoother
     - filterBeta: 10 (default), lower = less jittery
     Example: ?filterMinCF=0.2&filterBeta=5 (very smooth, slight lag)

   Example:
   ?scene=clipping-demo&camY=3&camZ=8&grid=true&axes=true
   ?portraitScale=1.6  (test portrait scenes at 60% larger scale)
   ?filterMinCF=0.15&filterBeta=8  (reduce jitter slightly)
`);
